import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, withQuery } from '../../core/client';
import type {
  CropCategory,
  ModelStageBody,
  StageVocabEntry,
  ActivationResult,
  CropProfile,
  CultivationResponse,
  DataFlag,
  ImportReport,
  ManualImportReport,
  NewProfileBody,
  ProfileDetail,
  ProfileStage,
  BandInput,
  RetainedBatch,
  StageInput,
  CropParameters,
  CropParameterTemplate,
  ApplyTemplateBody,
  ApplyTemplateResult,
} from './cultivation.types';

const BASE = '/users/v1/admin/cultivation';
const KEY = ['cultivation'] as const;
const PROFILES = ['cultivation', 'profiles'] as const;
const PARAMETERS = ['cultivation', 'parameters'] as const;
const TEMPLATES = ['cultivation', 'parameter-templates'] as const;

/** Categories, stage vocabulary, capacity specs and conflict flags in one call —
 * cultivation reference data is tens of rows, so one round trip beats four. */
export function useCultivationReference() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<CultivationResponse>(BASE) });
}

export function useCropProfiles(cropId?: string) {
  return useQuery({
    queryKey: [...PROFILES, cropId ?? 'all'],
    queryFn: () => api.get<CropProfile[]>(withQuery(`${BASE}/profiles`, { crop_id: cropId })),
  });
}

/** The profile with its stage plan and every band — they are meaningless apart. */
export function useCropProfile(id: string | null) {
  return useQuery({
    queryKey: [...PROFILES, 'detail', id],
    queryFn: () => api.get<ProfileDetail>(`${BASE}/profiles/${id}`),
    enabled: Boolean(id),
  });
}

// ── R-251: cultivation models ───────────────────────────────────────────────
//
/** Replace the farm's whole stage vocabulary. Wholesale, like a stage plan: the
 *  list is one ordered object.
 *
 *  ⚠ **R-315 removed cultivation models entirely**, and with them
 *  `useUpsertCultivationModel` / `useDeleteCultivationModel` and the per-model
 *  `PUT /models/{code}/stages`. There is ONE vocabulary now, at its own path —
 *  `stage-vocab`, NOT `stages`, because `/cultivation/stages/{id}/bands` already
 *  exists and its `{id}` is a PROFILE stage.
 *
 *  ⚠ Also invalidates PROFILES — a recipe's plan is checked for membership against
 *  this list. */
export function useReplaceStageVocab() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stages: ModelStageBody[]) => api.put<StageVocabEntry[]>(`${BASE}/stage-vocab`, stages),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: PROFILES });
    },
  });
}

export function useUpsertCropCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (record: CropCategory) => api.post<CropCategory>(`${BASE}/categories`, record),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteCropCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Always creates a DRAFT at the next version — an edit never touches a profile
 * that is already governing rooms. */
export function useCreateCropProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NewProfileBody) => api.post<CropProfile>(`${BASE}/profiles`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES }),
  });
}

/**
 * Save the whole plan — stages, their blocks, and every block's limits — in ONE
 * request.
 *
 * **R-316.** It used to take two: write the stages, then write each stage's bands
 * keyed by the ids that came back. That left a window where a stored plan was
 * half itself, and made the caller match returned ids to its own inputs. The
 * tiers only mean anything together — a stage with no blocks has no duration, a
 * band with no block has nothing to apply to — so they are written together.
 */
export function useReplaceStages(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stages: StageInput[]) =>
      api.put<ProfileStage[]>(`${BASE}/profiles/${profileId}/stages`, stages),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES }),
  });
}

export function useReplaceBands(stageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bands: BandInput[]) => api.put<number>(`${BASE}/stages/${stageId}/bands`, bands),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES }),
  });
}

/** The horticulture sign-off: the approver is whoever is logged in. */
/** R-176 — what activating this profile would leave behind, read BEFORE the
 *  activate call so the operator is warned rather than informed afterwards.
 *
 *  Imperative rather than a `useQuery`, because it is asked once at the moment
 *  the operator clicks Activate and must be FRESH: a cached answer from before
 *  someone else published a batch would understate the consequence.
 *
 *  The list comes from the server on purpose — it reads the same
 *  `batches.profile_id` the evaluator resolves bands by, so it cannot disagree
 *  with what is really governing a crop. Never recompute this client-side. */
export function fetchActivationPreview(profileId: string) {
  return api.get<RetainedBatch[]>(`${BASE}/profiles/${profileId}/activation-preview`);
}

/** Activate a draft, optionally moving crops already growing onto it.
 *
 *  `applyToRunning` defaults to FALSE and the caller must opt in deliberately.
 *  Versioning the recipe (R-224) means a running batch keeps the agronomy it was
 *  sown under; this is the recorded correction path for when that is wrong, and
 *  the reason it is a flag on the activation rather than a schema rule is that
 *  the operator is the only one who knows which case they are in.
 *
 *  Invalidates the BATCH-facing queries too when it moved something — the
 *  production screens read `profile_id`, and leaving them stale would show a
 *  crop still on the old version it no longer runs. */
export function useActivateCropProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, applyToRunning = false }: { id: string; applyToRunning?: boolean }) =>
      api.post<ActivationResult>(`${BASE}/profiles/${id}/activate`, { apply_to_running: applyToRunning }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES }),
  });
}

/** Discard a DRAFT recipe, with its stage plan, blocks and bands.
 *
 *  ⚠ **Draft only.** The backend refuses an `active` profile (it is governing
 *  rooms right now) and a `retired` one (it is the version some batch was sown
 *  under, and what every past judgement was made against) — the way out of an
 *  active profile is to activate a successor. It also refuses a draft any batch
 *  or plan still points at, naming the counts, so the caller can show the reason
 *  rather than a generic failure. Do not pre-judge either rule here: the server
 *  owns them and a second copy would drift. */
export function useDeleteCropProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES }),
  });
}

/** R-216 — every line default. A template is COPIED into a new crop and read by
 *  nothing else, so editing one changes no crop that already exists. */
export function useCropParameterTemplates() {
  return useQuery({
    queryKey: TEMPLATES,
    queryFn: () => api.get<CropParameterTemplate[]>(`${BASE}/parameter-templates`),
  });
}

export function useUpsertCropParameterTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (t: CropParameterTemplate) =>
      api.post<CropParameterTemplate>(`${BASE}/parameter-templates`, t),
    // Templates only — deliberately NOT invalidating PARAMETERS or PROFILES.
    // Nothing a crop shows can have changed, and refetching them would suggest
    // otherwise.
    onSuccess: () => void qc.invalidateQueries({ queryKey: TEMPLATES }),
  });
}

export function useDeleteCropParameterTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`${BASE}/parameter-templates/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: TEMPLATES }),
  });
}

/** COPY a line's defaults onto one crop, which then owns them. Refuses a crop
 *  that already has a recipe unless `overwrite` is set. */
export function useApplyCropParameterTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ApplyTemplateBody) =>
      api.post<ApplyTemplateResult>(`${BASE}/parameter-templates/apply`, b),
    // This one DOES change a crop, and its resolved stages with it.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARAMETERS });
      void qc.invalidateQueries({ queryKey: PROFILES });
    },
  });
}

/** R-216 — every parameter row. Small by construction (one base row per crop plus
 *  a row per stage that genuinely differs), so the console fetches the whole set
 *  rather than asking per crop. */
export function useCropParameters() {
  return useQuery({ queryKey: PARAMETERS, queryFn: () => api.get<CropParameters[]>(`${BASE}/parameters`) });
}

export function useUpsertCropParameters() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CropParameters) => api.post<CropParameters>(`${BASE}/parameters`, p),
    // Profiles too: a stage's RESOLVED agronomy changes when its line does.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARAMETERS });
      void qc.invalidateQueries({ queryKey: PROFILES });
    },
  });
}

export function useDeleteCropParameters() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`${BASE}/parameters/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PARAMETERS });
      void qc.invalidateQueries({ queryKey: PROFILES });
    },
  });
}

/** Accepting a value that knowingly differs from the source document. */
export function useSignOffDataFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<DataFlag>(`${BASE}/flags/${code}/signoff`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Raw CSV upload. `dry_run` reports the identical counts and writes nothing. */
export function useImportClimateMatrix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, dryRun }: { csv: string; dryRun: boolean }) =>
      api.post<ImportReport>(withQuery(`${BASE}/matrix/import`, { dry_run: String(dryRun) }), csv, {
        headers: { 'Content-Type': 'text/csv' },
      }),
    onSuccess: (_r, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: PROFILES });
    },
  });
}

/**
 * The 15 non-microgreen crops (conflict C-8), which have no spreadsheet.
 *
 * Send no CSV to import the file shipped with the build — the one-click path.
 * Send one to load a corrected file after horticulture review, without waiting
 * for a redeploy.
 */
export function useImportManualProfiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ csv, dryRun }: { csv?: string | undefined; dryRun: boolean }) =>
      api.post<ManualImportReport>(
        withQuery(`${BASE}/manual-profiles/import`, { dry_run: String(dryRun) }),
        csv ?? '',
        {
          headers: { 'Content-Type': 'text/csv' },
        },
      ),
    onSuccess: (_r, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: PROFILES });
    },
  });
}
